import {
  useOnboardingAnalytics,
  useOnboardingStepView,
} from "analytics/onboarding";
import useMedia from "utils/hooks/useMedia";
import styled from "styled-components";
import { softPanel } from "features/onboarding/common/onboardingUi";
import Button from "common/Buttons/Button";
import { scrollToTop } from "utils/hooks/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { OnboardingContext } from "@shared/features/onboarding/Onboarding.context";
import { OnboardingDetailedStep } from "@shared/features/onboarding/types";
import Text from "common/Typography/Text";
import useQuery from "utils/hooks/useQuery";
import {
  ONBOARDING_CONNECT_CHANNEL_ID,
  ONBOARDING_CHANNEL_TAB_ID,
} from "testIds";
import React, { useContext, useEffect, useState } from "react";
import SignalsGroupsSection from "features/onboarding/methodSettings/signalsGroup/groupCards/SiganlsGroupsSection";
import { TradingMethodType } from "@shared/features/onboarding/types";
import MethodSettingsLayout from "features/onboarding/methodSettings/MethodSettingsLayout";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { OnboardingStep } from "analytics/events";
import { GroupsPageContext } from "@shared/features/groups/GroupsPage.context";
import MissingGroupButton from "features/configuration/AccountsAndGroups/MissingGroupButton.tsx";
import Link from "common/Typography/Link";
import { typography } from "@shared/common/Themes/typography";
import { useSelector } from "react-redux";
import { sharedLib } from "@shared/services/shared.service";
import { VIRTUAL_CARDS_TITLE_CLASS } from "common/Lists/VirtualCardsList";

const FollowSignalGroup = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  // Phones: the tabs name the list, so its own title goes.
  const isPhone = useMedia(["(max-width: 560px)"], [true], false);
  const [selectedTab, setChannelTab] = useState<ChannelTab>();
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  // Telegram or Discord is already connected: connecting another is a link
  // beside My channels, not the step's lead card.
  const isConnected = !!user?.hasExternalAccount();
  const { analytics } = useContext(AnalyticsContext);
  const { myGroupsData, freeTrialGroupsData, freeGroupsData } =
    useContext(GroupsPageContext)!;

  const { changeStep } = useContext(OnboardingContext)!;
  const query = useQuery();
  // No free channels to offer: no tab for them.
  const tabs = CHANNEL_TABS.filter(
    (tab) =>
      tab !== "discover" ||
      freeGroupsData.isLoading ||
      !!freeGroupsData.groups?.length
  );
  const requestedTab = query.get("channelTab") as ChannelTab | null;
  const channelTab =
    (selectedTab && tabs.includes(selectedTab) && selectedTab) ||
    (requestedTab && tabs.includes(requestedTab) && requestedTab) ||
    (myGroupsData.groups?.length ? "my" : "trial");

  const track = useOnboardingAnalytics();
  useOnboardingStepView("channel_selection");
  const connectChannel = (placement: "card" | "link") => {
    track("Connect Channel Clicked", { placement });
    changeStep(OnboardingDetailedStep.CONNECT_EXTERNAL_ACCOUNT, {
      channelTab: "my",
    });
  };
  const changeTab = (tab: ChannelTab) => {
    if (tab !== channelTab) track("Channel Tab Changed", { tab });
    setChannelTab(tab);
  };

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    analytics?.onboardingConnectGroupView({
      onboardingStep: OnboardingStep.METHOD_SETTINGS,
    });
  }, [analytics]);

  const options = (
    <>
      {!isConnected && (
        <ChannelEntry>
          <div>
            <Text style={{ fontWeight: 600 }}>
              {t("revision.own_channel_title")}
            </Text>
            <Text type="medium">{t("revision.own_channel_description")}</Text>
          </div>
          <Button
            data-testid={ONBOARDING_CONNECT_CHANNEL_ID}
            onClick={() => connectChannel("card")}
          >
            {t("revision.connect_channel")}
          </Button>
        </ChannelEntry>
      )}
      <ChannelTabs $count={tabs.length}>
        {tabs.map((tab) => (
          <Button
            key={tab}
            type={channelTab === tab ? "primary" : "default"}
            aria-pressed={channelTab === tab}
            data-testid={`${ONBOARDING_CHANNEL_TAB_ID}-${tab}`}
            onClick={() => changeTab(tab)}
          >
            {t(
              tab === "my"
                ? "intent.my_channels"
                : tab === "discover"
                ? "intent.discover_channels"
                : "intent.trial_channels"
            )}
          </Button>
        ))}
      </ChannelTabs>
      <ChannelPanel>
        {channelTab === "my" && (
          <SignalsGroupsSection
            {...myGroupsData}
            hideTitle={isPhone}
            extraButton={
              <ChannelLinks>
                <MissingGroupButton isLink={true} />
                {isConnected && (
                  <Link
                    data-testid={ONBOARDING_CONNECT_CHANNEL_ID}
                    onClick={() => connectChannel("link")}
                    style={{ fontSize: typography.fontSizes.S }}
                  >
                    {t("revision.connect_another")}
                  </Link>
                )}
              </ChannelLinks>
            }
          />
        )}
        {channelTab === "trial" && (
          <SignalsGroupsSection {...freeTrialGroupsData} hideTitle={isPhone} />
        )}
        {channelTab === "discover" && (
          <SignalsGroupsSection {...freeGroupsData} hideTitle={isPhone} />
        )}
      </ChannelPanel>
    </>
  );

  return (
    <MethodSettingsLayout
      tradingMethod={TradingMethodType.SIGNALS_GROUP}
      options={options}
    />
  );
};

export default FollowSignalGroup;

type ChannelTab = "my" | "discover" | "trial";
const CHANNEL_TABS: ChannelTab[] = ["trial", "my", "discover"];

const ChannelLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 16px;
  margin: 4px 8px 0;
`;
const ChannelTabs = styled.div<{ $count: number }>`
  display: inline-flex;
  justify-content: flex-start;
  width: auto;
  max-width: 100%;
  gap: 2px;
  margin-bottom: 16px;
  padding: 3px;
  border-radius: ${(p) => p.theme.radii.control}px;
  background: ${(p) => p.theme.colors.cardLightBgColor};
  border: 0;
  && .ant-btn {
    height: 30px;
    padding: 0 14px;
    border: 0;
    border-radius: 6px;
    box-shadow: none;
    background: transparent !important;
    color: ${(p) => p.theme.colors.secondaryText} !important;
    font-size: 13px;
    font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  }
  && .ant-btn:hover {
    color: ${(p) => p.theme.colors.titleColor} !important;
  }
  && .ant-btn[aria-pressed="true"] {
    background: ${(p) => p.theme.colors.cardBgColor} !important;
    color: ${(p) => p.theme.colors.titleColor} !important;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.08);
  }
  @media (max-width: 650px) {
    display: grid;
    grid-template-columns: repeat(${(p) => p.$count}, minmax(0, 1fr));
    gap: 8px;
    width: 100%;
    box-sizing: border-box;
    button {
      min-width: 0;
    }
    && .ant-btn {
      min-height: 44px;
      height: auto;
      padding: 6px;
      white-space: normal;
      line-height: 1.25;
    }
  }
  /* Very narrow phones: one line per tab ("My channels" wrapped). */
  @media (max-width: 360px) {
    gap: 4px;
    && .ant-btn {
      padding: 4px;
      font-size: 12px;
      white-space: nowrap;
    }
  }
`;
const ChannelPanel = styled.div`
  width: 100%;
  padding: 0;
  margin-bottom: 24px;
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  /* Phones: the card list's own padding (20px all round) goes, so the
     card sits near the page's gutter and right under the tabs. */
  @media (max-width: 560px) {
    margin-bottom: 12px;
    .swiper {
      padding: 4px 0 4px !important;
    }
    /* Its title row lines up with the cards (no list padding either). */
    .${VIRTUAL_CARDS_TITLE_CLASS} {
      margin: 0 !important;
    }
    ${ChannelLinks} {
      margin: 0;
      width: 100%;
      justify-content: space-between;
    }
    /* Its boxes are sized for that padding (inline heights); fit the
       cards instead. */
    div:has(.swiper) {
      height: auto !important;
      min-height: 0 !important;
    }
  }
`;

const ChannelEntry = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  ${softPanel};
  padding: 20px 24px;
  margin-bottom: 20px;
  p {
    line-height: 1.5;
  }
  @media (max-width: 650px) {
    flex-direction: column;
    align-items: stretch;
    padding: 16px;
    gap: 12px;
    margin-bottom: 16px;
    p {
      font-size: 14px;
      margin: 0;
    }
  }
`;
